import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Editor, useEditorState } from '@tiptap/react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore, TaskAlt, ManageSearch, OpenInNew, Search, Spellcheck, Dangerous } from '@mui/icons-material';
import { useGuideCmsContext } from '../../GuideCmsContext';
import { SidebarHeading } from '../SidebarHeading';
import { StyledDrawerContent } from '../references/styles';
import { GET_ALL } from '../../../../dictionary/dictionary.graphql';
import { searchTerms } from '../../../../dictionary/search';
import { RichText, htmlIsEmpty } from '../../../../components/richText';
import { TermItemFragment } from '../../../../__generated__/graphql';
import { DictionaryMatch, getDictionaryMatches } from '../../tiptap/extensions/plugins/DictionaryHighlight';

enum DictionaryTabs {
  InUse = 0,
  Rejected = 1,
  Lookup = 2,
}

/** The dictionary in the CMS: the terms used in the guide, the nei-ord used, and lookup while writing. */
export const DictionarySidebar: FC<{ editor: Editor }> = ({ editor }) => {
  const { dictionaryRequest } = useGuideCmsContext();
  const [tab, setTab] = useState(dictionaryRequest ? DictionaryTabs.Lookup : DictionaryTabs.InUse);
  const accepted = useEditorState({ editor, selector: (ctx) => getDictionaryMatches(ctx.editor.state, 'accepted') }) ?? [];
  const rejected = useEditorState({ editor, selector: (ctx) => getDictionaryMatches(ctx.editor.state, 'rejected') }) ?? [];

  // Clicking a word in the text opens its term in Oppslag.
  useEffect(() => {
    if (dictionaryRequest) setTab(DictionaryTabs.Lookup);
  }, [dictionaryRequest]);

  return (
    <>
      <Tabs sx={{ minHeight: '68px' }} variant="fullWidth" value={tab} onChange={(_e, value) => setTab(value)}>
        <Tab
          value={DictionaryTabs.InUse}
          label={
            <>
              <TaskAlt />
              <Typography variant="caption">I bruk</Typography>
            </>
          }
        />
        <Tab
          value={DictionaryTabs.Rejected}
          label={
            <Badge color="error" variant="dot" overlap="circular" invisible={rejected.length === 0}>
              <Stack alignItems="center">
                <Dangerous />
                <Typography variant="caption">Nei-ord ({rejected.length})</Typography>
              </Stack>
            </Badge>
          }
        />
        <Tab
          value={DictionaryTabs.Lookup}
          label={
            <>
              <ManageSearch />
              <Typography variant="caption">Oppslag</Typography>
            </>
          }
        />
      </Tabs>
      <StyledDrawerContent>
        {tab === DictionaryTabs.InUse && <TermsInUse editor={editor} matches={accepted} />}
        {tab === DictionaryTabs.Rejected && <RejectedTerms editor={editor} matches={rejected} />}
        {tab === DictionaryTabs.Lookup && <Lookup />}
      </StyledDrawerContent>
    </>
  );
};

const Lookup: FC = () => {
  const { dictionaryRequest } = useGuideCmsContext();
  const { data } = useQuery(GET_ALL);
  const terms = useMemo(() => (data?.terms ?? []).filter((t): t is TermItemFragment => !!t), [data]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  // A search from the bubble menu, or a term clicked in the text.
  useEffect(() => {
    if (dictionaryRequest?.search !== undefined) {
      setSearch(dictionaryRequest.search);
      setExpanded(null);
      return;
    }
    const term = terms.find((t) => t.id === dictionaryRequest?.termId);
    if (term) {
      setSearch(term.preferred ?? '');
      setExpanded(term.id);
    }
  }, [dictionaryRequest, terms]);

  const hits = useMemo(() => searchTerms(terms, search), [terms, search]);

  return (
    <div>
      <SidebarHeading icon={<Spellcheck />}>Ordliste</SidebarHeading>
      <TextField
        type="search"
        size="small"
        placeholder="Søk i ordlista..."
        value={search}
        onChange={(e) => {
          // Typing or clearing starts a new search, so close the open term.
          setSearch(e.target.value);
          setExpanded(null);
        }}
        fullWidth
        sx={{ mb: 2 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      {hits.length === 0 && <Alert severity="info">Fant ingen begreper for «{search}».</Alert>}
      <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
        {hits.map(({ term, synonymMatch }) => (
          <Accordion
            key={term.id}
            disableGutters
            elevation={0}
            square
            expanded={expanded === term.id}
            onChange={(_e, open) => setExpanded(open ? term.id : null)}
            sx={{ borderBottom: 1, borderColor: 'divider', '&::before': { display: 'none' } }}>
            <AccordionSummary
              expandIcon={<ExpandMore fontSize="small" />}
              sx={{
                minHeight: 40,
                px: 1,
                '&.Mui-expanded': { bgcolor: 'action.hover' },
                '& .MuiAccordionSummary-content': { my: 0.75 },
              }}>
              <Stack>
                <Typography variant="body2">{term.preferred}</Typography>
                {synonymMatch && (
                  <Typography variant="caption" color="textSecondary">
                    Alternativ: {synonymMatch}
                  </Typography>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1, pt: 1.5, pb: 2 }}>
              <TermSummary term={term} />
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </div>
  );
};

const TermSummary: FC<{ term: TermItemFragment }> = ({ term }) => {
  const section = (label: string, content: React.ReactNode) => (
    <div>
      <Typography variant="caption" color="textSecondary" component="div" sx={{ mb: 0.25 }}>
        {label}
      </Typography>
      <Box sx={{ typography: 'body2', '& p': { mt: 0, mb: 0.5 }, '& p:last-of-type': { mb: 0 } }}>{content}</Box>
    </div>
  );
  return (
    <Stack spacing={1.5}>
      {!!term.synonyms?.length &&
        section(
          'Alternativ (vi velger ikke)',
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {term.synonyms.map((s) => (
              <Chip key={s} size="small" label={s} />
            ))}
          </Stack>,
        )}
      {!htmlIsEmpty(term.definition) && section('Definisjon', <RichText html={term.definition} component="div" />)}
      {!htmlIsEmpty(term.notes) && section('Merknad om bruk', <RichText html={term.notes} component="div" />)}
      <Link
        href={`/dictionary/${term.id}`}
        target="_blank"
        rel="noopener"
        variant="body2"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        Åpne i ordlista <OpenInNew fontSize="inherit" />
      </Link>
    </Stack>
  );
};

type MatchGroup = { key: string; termId: string; preferred: string; occurrences: DictionaryMatch[] };

const groupBy = (matches: DictionaryMatch[], keyOf: (m: DictionaryMatch) => string): MatchGroup[] => {
  const byKey = new Map<string, MatchGroup>();
  for (const m of matches) {
    const key = keyOf(m);
    const group = byKey.get(key) ?? { key, termId: m.termId, preferred: m.preferred, occurrences: [] };
    group.occurrences.push(m);
    byKey.set(key, group);
  }
  return [...byKey.values()];
};

/** Each click on a row selects the next occurrence of it in the text, and scrolls there. */
const useGoToNext = (editor: Editor) => {
  const nextIndex = useRef(new Map<string, number>());
  return (group: MatchGroup) => {
    const index = (nextIndex.current.get(group.key) ?? 0) % group.occurrences.length;
    nextIndex.current.set(group.key, index + 1);
    const { from, to } = group.occurrences[index];
    editor.chain().focus().setTextSelection({ from, to }).scrollIntoViewWithOffset().run();
  };
};

const HighlightSwitch: FC<{ label: string; checked: boolean; onChange: (checked: boolean) => void }> = ({ label, checked, onChange }) => (
  <FormControlLabel control={<Switch checked={checked} onChange={(e) => onChange(e.target.checked)} />} label={label} />
);

/**
 * One row in «I bruk» and «Nei-ord»: the word (click to go to its next occurrence), what it points
 * to, how many times it is used, and a button to open the term in Oppslag.
 */
const MatchRow: FC<{
  group: MatchGroup;
  label: React.ReactNode;
  color: 'success' | 'error';
  detail?: React.ReactNode;
  onGoToNext: (group: MatchGroup) => void;
}> = ({ group, label, color, detail, onGoToNext }) => {
  const { openDictionaryTerm } = useGuideCmsContext();
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Button size="small" color={color} onClick={() => onGoToNext(group)} sx={{ textTransform: 'none', minWidth: 0 }}>
        {label}
      </Button>
      {detail && (
        <Typography variant="body2" color="textSecondary" noWrap>
          {detail}
        </Typography>
      )}
      <Chip size="small" label={group.occurrences.length} title="Antall i dokumentet" />
      <IconButton size="small" title="Vis i oppslag" onClick={() => openDictionaryTerm(group.termId)} sx={{ ml: 'auto !important' }}>
        <ManageSearch fontSize="small" />
      </IconButton>
    </Stack>
  );
};

/** Shared layout for «I bruk» and «Nei-ord»: heading, highlight switch, explanation, rows. */
const MatchPanel: FC<{
  icon: React.ReactNode;
  heading: string;
  switchLabel: string;
  highlight: boolean;
  onHighlightChange: (checked: boolean) => void;
  isEmpty: boolean;
  emptyAlert: React.ReactNode;
  alert: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, heading, switchLabel, highlight, onHighlightChange, isEmpty, emptyAlert, alert, children }) => (
  <div>
    <SidebarHeading icon={icon}>{heading}</SidebarHeading>
    <HighlightSwitch label={switchLabel} checked={highlight} onChange={onHighlightChange} />
    <Box sx={{ mb: 1 }}>{isEmpty ? emptyAlert : alert}</Box>
    <Stack spacing={0.5}>{children}</Stack>
  </div>
);

const TermsInUse: FC<{ editor: Editor; matches: DictionaryMatch[] }> = ({ editor, matches }) => {
  const { isDictionaryTermHighlightEnabled, setIsDictionaryTermHighlightEnabled } = useGuideCmsContext();
  const goToNext = useGoToNext(editor);

  useEffect(() => {
    if (!editor.isDestroyed) editor.commands.setDictionaryHighlight({ accepted: isDictionaryTermHighlightEnabled });
  }, [editor, isDictionaryTermHighlightEnabled]);

  const groups = useMemo(() => groupBy(matches, (m) => m.termId).sort((a, b) => b.occurrences.length - a.occurrences.length), [matches]);

  return (
    <MatchPanel
      icon={<TaskAlt />}
      heading="Begreper i dokumentet"
      switchLabel="Markér begreper i teksten"
      highlight={isDictionaryTermHighlightEnabled}
      onHighlightChange={setIsDictionaryTermHighlightEnabled}
      isEmpty={groups.length === 0}
      emptyAlert={<Alert severity="info">Ingen begreper fra ordlista i dokumentet.</Alert>}
      alert={<Alert severity="info">Begreper fra ordlista som brukes i dokumentet. Klikk på et ord for å gå til det.</Alert>}>
      {groups.map((g) => {
        // Synonyms that matched, e.g. «glassfasade ×2» under påhengsvegg.
        const synonyms = groupBy(
          g.occurrences.filter(
            (m) => m.word.toLowerCase() !== g.preferred.toLowerCase() && !g.preferred.toLowerCase().startsWith(m.word.toLowerCase()),
          ),
          (m) => m.word.toLowerCase(),
        );
        const detail = synonyms.length > 0 ? `(${synonyms.map((s) => `${s.occurrences[0].word} ×${s.occurrences.length}`).join(', ')})` : undefined;
        return <MatchRow key={g.key} group={g} label={g.preferred} color="success" detail={detail} onGoToNext={goToNext} />;
      })}
    </MatchPanel>
  );
};

const RejectedTerms: FC<{ editor: Editor; matches: DictionaryMatch[] }> = ({ editor, matches }) => {
  const { isRejectedTermHighlightEnabled, setIsRejectedTermHighlightEnabled } = useGuideCmsContext();
  const goToNext = useGoToNext(editor);

  useEffect(() => {
    if (!editor.isDestroyed) editor.commands.setDictionaryHighlight({ rejected: isRejectedTermHighlightEnabled });
  }, [editor, isRejectedTermHighlightEnabled]);

  const groups = useMemo(
    () => groupBy(matches, (m) => `${m.termId}:${m.word.toLowerCase()}`).sort((a, b) => b.occurrences.length - a.occurrences.length),
    [matches],
  );

  return (
    <MatchPanel
      icon={<Dangerous />}
      heading="Nei-ord i dokumentet"
      switchLabel="Markér nei-ord i teksten"
      highlight={isRejectedTermHighlightEnabled}
      onHighlightChange={setIsRejectedTermHighlightEnabled}
      isEmpty={groups.length === 0}
      emptyAlert={<Alert severity="success">Ingen nei-ord i dokumentet.</Alert>}
      alert={<Alert severity="warning">Ord som ordlista anbefaler å erstatte. Klikk på et ord for å gå til det.</Alert>}>
      {groups.map((g) => (
        <MatchRow key={g.key} group={g} label={`«${g.occurrences[0].word}»`} color="error" detail={`→ ${g.preferred}`} onGoToNext={goToNext} />
      ))}
    </MatchPanel>
  );
};
